import { Game } from "./engine/game";
import { generateProdLevel } from "./engine/levelgenerator/prodLevelGenerator";
import { TutorialLevelGenerator } from "./engine/levelgenerator/tutorialLevelGenerator";
import { PlaygroundLevelGenerator } from "./engine/levelgenerator/playgroundLevelGenerator";
import { generateSeedString } from "./engine/random";
import { Player } from "./engine/player";
import { BarrierGun } from "./engine/weapons/barrierGun";
import { CannonBurst } from "./engine/weapons/cannonBurst";
import { Cannon } from "./engine/weapons/cannon";
import { Solidifier } from "./engine/weapons/solidifier";
import { RockGun } from "./engine/weapons/rockGun";
import { DevGunAutoKill } from "./engine/weapons/devGunAutoKill";
import { DrillLauncher } from "./engine/weapons/drillLauncher";
import { PlasmaGun } from "./engine/weapons/plasmaGun";
import { Flamethrower } from "./engine/weapons/flamethrower";
import { GasBombWeapon } from "./engine/weapons/gasBombWeapon";
import { Key } from "./engine/items/key";
import { Medikit } from "./engine/items/medikit";
import { NightVisionEquipment } from "./engine/items/nightVisionEquipment";
import { LandmineWeapon } from "./engine/weapons/landmineWeapon";
import { LaserGun } from "./engine/weapons/laserGun";
import { MissileBurst } from "./engine/weapons/missileBurst";
import { MissileGun } from "./engine/weapons/missileGun";
import { OpGun } from "./engine/weapons/opGun";
import { OpGun2 } from "./engine/weapons/opGun2";
import { BlackHoleGun } from "./engine/weapons/blackHoleGun";
import { OpGunNuke } from "./engine/weapons/opGunNuke";
import { TripleSolidifier } from "./engine/weapons/tripleSolidifier";
import type { Weapon } from "./engine/weapon";
import { LevelChangerThing } from "./engine/things/levelChangerThing";
import { AirBlast } from "./engine/weapons/airBlast";
import { CryoGun } from "./engine/weapons/cryoGun";
import { Jetpack } from "./engine/weapons/jetpack";
import { Sword } from "./engine/weapons/sword";
import { Cleaver } from "./engine/weapons/cleaver";
import { XY } from "./engine/xy.ts";
import { DEFAULT_CHARACTER_ID, getCharacterDef } from "./engine/characterDefs";
import { loadSeenHints } from "./ui/hintStorage";
import { isDevMode, getSeedFromUrl } from "./devParams";

export const SAVE_KEY = "bullettime-save";

export { isDevMode, getSeedFromUrl };

function seedSeenHints(game: Game): void {
	for (const id of loadSeenHints()) game.seenHints.add(id);
}

export function addWeaponWithAmmo(player: Player, weapon: Weapon, ammo: number): void {
	weapon.ammo = ammo;
	player.addWeaponToArsenal(weapon);
}

export function addDevLoadout(player: Player): void {
	const AMMO = 200;
	addWeaponWithAmmo(player, new DevGunAutoKill(player), AMMO);
	addWeaponWithAmmo(player, new Solidifier(player), AMMO);
	addWeaponWithAmmo(player, new CryoGun(player), AMMO);
	addWeaponWithAmmo(player, new TripleSolidifier(player), AMMO);
	addWeaponWithAmmo(player, new MissileGun(player), AMMO);
	addWeaponWithAmmo(player, new MissileBurst(player), AMMO);
	addWeaponWithAmmo(player, new Cannon(player), AMMO);
	addWeaponWithAmmo(player, new CannonBurst(player), AMMO);
	addWeaponWithAmmo(player, new PlasmaGun(player), AMMO);
	addWeaponWithAmmo(player, new DrillLauncher(player), AMMO);
	addWeaponWithAmmo(player, new Flamethrower(player), AMMO);
	addWeaponWithAmmo(player, new GasBombWeapon(player), AMMO);
	addWeaponWithAmmo(player, new LaserGun(player), AMMO);
	addWeaponWithAmmo(player, new LandmineWeapon(player), AMMO);
	addWeaponWithAmmo(player, new RockGun(player), AMMO);
	addWeaponWithAmmo(player, new BarrierGun(player), AMMO);
	addWeaponWithAmmo(player, new BlackHoleGun(player), AMMO);
	addWeaponWithAmmo(player, new OpGun(player), AMMO);
	addWeaponWithAmmo(player, new OpGun2(player), AMMO);
	addWeaponWithAmmo(player, new OpGunNuke(player), AMMO);
	addWeaponWithAmmo(player, new AirBlast(player), AMMO);
	addWeaponWithAmmo(player, new Sword(player), AMMO);
	addWeaponWithAmmo(player, new Cleaver(player), AMMO);
	addWeaponWithAmmo(player, new Jetpack(player), AMMO);

	player.inventory.addItem(new Key(99));
	player.inventory.addItem(new Medikit(99));
	player.inventory.addItem(new NightVisionEquipment(1, true));

	player.selectWeaponFamily(2); // Default to Cannon

	player.isInvulnerable = true;
}

export function createNewGame(startLevel: number = 1, seed?: string, characterId: string = DEFAULT_CHARACTER_ID): Game {
	const character = getCharacterDef(characterId) ?? getCharacterDef(DEFAULT_CHARACTER_ID)!;
	const seedStr = seed || getSeedFromUrl() || generateSeedString();
	const game = new Game(seedStr);
	game.characterId = character.id;
	seedSeenHints(game);
	const board = generateProdLevel(startLevel, game.mapRngForLevel(startLevel), game.seed);

	let spawnXY = board.initialSpawnXY;
	if (!spawnXY && isDevMode()) {
		// In dev mode starting at an arbitrary level, spawn at a level changer
		// that points to a previous level (i.e. the "entrance" to this level)
		const entrance = board
			.getPhysicalThings()
			.find((t) => t instanceof LevelChangerThing && t.destinationLevel < startLevel);
		if (entrance) spawnXY = entrance.xy;
	}
	if (!spawnXY) {
		throw new Error(`No spawn point found for level ${startLevel}`);
	}
	const player = new Player(spawnXY);

	if (isDevMode()) {
		addDevLoadout(player);
	} else {
		character.applyLoadout(player);
	}

	game.player = player;
	game.levelGeneratorFn = (n, mapRng) => generateProdLevel(n, mapRng, game.seed);
	game.addLevel(startLevel, board);
	game.currentLevelNumber = startLevel;
	if (isDevMode() && startLevel >= 5) {
		game.gameFlags.activatedPowerPlant = true;
	}
	board.addThing(player);
	return game;
}

export function createTutorialGame(): Game {
	const tutorialGenerator = new TutorialLevelGenerator();
	const board = tutorialGenerator.generateNewLevel(0);

	const player = new Player(board.initialSpawnXY || new XY(0, 0));
	// Tutorial player starts with zero weapons (loot boxes give weapons during play)

	const game = new Game();
	game.player = player;
	game.isTutorial = true;
	game.levelGeneratorFn = (n, mapRng) => tutorialGenerator.generateNewLevel(n);
	game.addLevel(0, board);
	game.currentLevelNumber = 0;
	board.addThing(player);
	return game;
}

export function createExperimentalGame(startLevel: number = 1, seed?: string): Game {
	const seedStr = seed || getSeedFromUrl() || generateSeedString();
	const game = new Game(seedStr);
	seedSeenHints(game);
	const board = generateProdLevel(startLevel, game.mapRngForLevel(startLevel), game.seed);

	let spawnXY = board.initialSpawnXY;
	if (!spawnXY && isDevMode()) {
		const entrance = board
			.getPhysicalThings()
			.find((t) => t instanceof LevelChangerThing && t.destinationLevel < startLevel);
		if (entrance) spawnXY = entrance.xy;
	}
	if (!spawnXY) {
		throw new Error(`No spawn point found for level ${startLevel}`);
	}
	const player = new Player(spawnXY);

	if (isDevMode()) {
		addDevLoadout(player);
	} else {
		// Experimental loadout
		player.addWeaponToArsenal(new AirBlast(player));
		player.addWeaponToArsenal(new CryoGun(player));
		const sword = new Sword(player);
		player.addWeaponToArsenal(sword);
		player.addWeaponToArsenal(new Cleaver(player));
		player.selectWeaponByIndex(player.arsenal.getAllWeapons().indexOf(sword));
	}

	game.player = player;
	game.levelGeneratorFn = (n, mapRng) => generateProdLevel(n, mapRng, game.seed);
	game.addLevel(startLevel, board);
	game.currentLevelNumber = startLevel;
	if (isDevMode() && startLevel >= 5) {
		game.gameFlags.activatedPowerPlant = true;
	}
	board.addThing(player);
	return game;
}

export function createPlaygroundGame(startLevel: number = 1): Game {
	const generator = new PlaygroundLevelGenerator();
	const board = generator.generateNewLevel(startLevel);

	const player = board.player ?? new Player(board.initialSpawnXY!);

	addDevLoadout(player);
	player.gold = 1000;

	const game = new Game();
	seedSeenHints(game);
	game.player = player;
	game.levelGeneratorFn = (n, mapRng) => generator.generateNewLevel(n);
	game.addLevel(startLevel, board);
	game.currentLevelNumber = startLevel;
	if (!board.player) board.addThing(player);
	return game;
}
